import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { ChatScreen } from './ChatScreen';
import { Users, UserPlus, Search, MessageCircle, Swords, Trophy, Clock } from 'lucide-react';
import { cn } from '../../lib/utils';
// Mock friends data
const FRIENDS_DATA = [{
  id: 1,
  name: 'Sarah Pro',
  status: 'online',
  level: 43,
  wins: 845,
  lastPlayed: '2 min ago',
  avatar: '⭐'
}, {
  id: 2,
  name: 'Mike Master',
  status: 'online',
  level: 41,
  wins: 798,
  lastPlayed: '5 min ago',
  avatar: '🎯'
}, {
  id: 3,
  name: 'Emma Swift',
  status: 'playing',
  level: 39,
  wins: 756,
  lastPlayed: 'Now',
  avatar: '⚡'
}, {
  id: 4,
  name: 'Chris Ace',
  status: 'offline',
  level: 38,
  wins: 723,
  lastPlayed: '1 hour ago',
  avatar: '🔥'
}, {
  id: 5,
  name: 'Lisa Quick',
  status: 'offline',
  level: 36,
  wins: 689,
  lastPlayed: '3 hours ago',
  avatar: '💫'
}];
const FRIEND_REQUESTS_DATA = [{
  id: 1,
  name: 'Tom Flash',
  level: 35,
  avatar: '🌟'
}, {
  id: 2,
  name: 'Nina Speed',
  level: 34,
  avatar: '✨'
}];
const SEARCH_RESULTS = [{
  id: 10,
  name: 'Alex Champion',
  level: 45,
  wins: 892,
  avatar: '🏆',
  isFriend: false
}, {
  id: 11,
  name: 'Kate Sharp',
  level: 32,
  wins: 556,
  avatar: '🎪',
  isFriend: false
}, {
  id: 12,
  name: 'Ryan Fast',
  level: 33,
  wins: 589,
  avatar: '💥',
  isFriend: false
}];
type FriendsTab = 'friends' | 'requests' | 'search';
export function FriendsScreen() {
  const [activeTab, setActiveTab] = useState<FriendsTab>('friends');
  const [searchQuery, setSearchQuery] = useState('');
  const [friends, setFriends] = useState(FRIENDS_DATA);
  const [friendRequests, setFriendRequests] = useState(FRIEND_REQUESTS_DATA);
  const [searchResults, setSearchResults] = useState<typeof SEARCH_RESULTS>([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState<{
    name: string;
    avatar: string;
  } | null>(null);
  const handleSearch = () => {
    if (searchQuery.trim()) {
      // Simulate search
      const filtered = SEARCH_RESULTS.filter(user => user.name.toLowerCase().includes(searchQuery.toLowerCase()));
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }
  };
  const handleAddFriend = (userId: number, userName: string) => {
    alert(`Friend request sent to ${userName}!`);
    // In production: API call to send friend request
  };
  const handleAcceptRequest = (requestId: number) => {
    const request = friendRequests.find(r => r.id === requestId);
    if (request) {
      setFriends([...friends, {
        id: request.id,
        name: request.name,
        status: 'offline',
        level: request.level,
        wins: 0,
        lastPlayed: 'Just now',
        avatar: request.avatar
      }]);
      setFriendRequests(friendRequests.filter(r => r.id !== requestId));
    }
  };
  const handleDeclineRequest = (requestId: number) => {
    setFriendRequests(friendRequests.filter(r => r.id !== requestId));
  };
  const handleOpenChat = (friend: {
    name: string;
    avatar: string;
  }) => {
    setSelectedFriend(friend);
    setChatOpen(true);
  };
  const handleChallenge = (friendName: string) => {
    alert(`Challenge sent to ${friendName}! They'll be notified.`);
    // In production: Create game room and send notification
  };
  const getStatusColor = (status: string) => {
    if (status === 'online') return 'bg-emerald-500';
    if (status === 'playing') return 'bg-blue-500 animate-pulse';
    return 'bg-slate-300';
  };
  const getStatusText = (status: string) => {
    if (status === 'online') return 'Online';
    if (status === 'playing') return 'In Game';
    return 'Offline';
  };
  return <div className="flex flex-col h-full bg-slate-50" data-id="element-254">
      {/* Header */}
      <div className="bg-gradient-to-br from-violet-500 to-purple-600 text-white p-6 pb-8" data-id="element-255">
        <div className="flex items-center justify-center gap-3 mb-4" data-id="element-256">
          <Users size={32} data-id="element-257" />
          <h1 className="text-3xl font-black" data-id="element-258">Friends</h1>
        </div>
        <p className="text-center text-violet-100" data-id="element-259">
          Play and compete with friends
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 px-4 -mt-4 rounded-t-3xl" data-id="element-260">
        <div className="flex gap-2 pt-4" data-id="element-261">
          {[{
          id: 'friends' as FriendsTab,
          label: '👥 Friends',
          count: friends.length
        }, {
          id: 'requests' as FriendsTab,
          label: '📬 Requests',
          count: friendRequests.length
        }, {
          id: 'search' as FriendsTab,
          label: '🔍 Search',
          count: null
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={cn('flex-1 py-3 px-4 rounded-t-xl font-semibold text-sm transition-colors relative', activeTab === tab.id ? 'bg-violet-50 text-violet-600 border-b-2 border-violet-600' : 'text-slate-500 hover:text-slate-700')} data-id="element-262">
              {tab.label}
              {tab.count !== null && tab.count > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center" data-id="element-263">
                  {tab.count}
                </span>}
            </button>)}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4" data-id="element-264">
        {activeTab === 'friends' && <div className="space-y-2" data-id="element-265">
            {friends.map(friend => <Card key={friend.id} className="hover:shadow-md transition-all" data-id="element-266">
                <div className="flex items-center gap-4" data-id="element-267">
                  {/* Avatar with status */}
                  <div className="relative flex-shrink-0" data-id="element-268">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center text-2xl" data-id="element-269">
                      {friend.avatar}
                    </div>
                    <div className={cn('absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white', getStatusColor(friend.status))} data-id="element-270" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0" data-id="element-271">
                    <div className="flex items-center gap-2 mb-1" data-id="element-272">
                      <h3 className="font-bold text-slate-900 truncate" data-id="element-273">
                        {friend.name}
                      </h3>
                      <Badge variant={friend.status === 'online' ? 'success' : friend.status === 'playing' ? 'info' : 'default'} className="text-xs" data-id="element-274">
                        {getStatusText(friend.status)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500" data-id="element-275">
                      <span className="flex items-center gap-1" data-id="element-276">
                        <Trophy size={12} data-id="element-277" />
                        Lvl {friend.level}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-278">
                        <Clock size={12} data-id="element-279" />
                        {friend.lastPlayed}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2" data-id="element-280">
                    <button onClick={() => handleOpenChat({
                name: friend.name,
                avatar: friend.avatar
              })} className="p-2 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors" data-id="element-281">
                      <MessageCircle size={20} data-id="element-282" />
                    </button>
                    <button onClick={() => handleChallenge(friend.name)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-283">
                      <Swords size={20} data-id="element-284" />
                    </button>
                  </div>
                </div>
              </Card>)}
          </div>}

        {activeTab === 'requests' && <div className="space-y-2" data-id="element-285">
            {friendRequests.length > 0 ? friendRequests.map(request => <Card key={request.id} data-id="element-286">
                  <div className="flex items-center gap-4" data-id="element-287">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center text-2xl" data-id="element-288">
                      {request.avatar}
                    </div>
                    <div className="flex-1" data-id="element-289">
                      <h3 className="font-bold text-slate-900" data-id="element-290">
                        {request.name}
                      </h3>
                      <p className="text-xs text-slate-500" data-id="element-291">
                        Level {request.level}
                      </p>
                    </div>
                    <div className="flex gap-2" data-id="element-292">
                      <Button size="sm" variant="primary" onClick={() => handleAcceptRequest(request.id)} data-id="element-293">
                        Accept
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeclineRequest(request.id)} data-id="element-294">
                        Decline
                      </Button>
                    </div>
                  </div>
                </Card>) : <div className="text-center py-12" data-id="element-295">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-296">
                  <Users className="text-slate-400" size={40} data-id="element-297" />
                </div>
                <p className="text-slate-500" data-id="element-298">No friend requests</p>
              </div>}
          </div>}

        {activeTab === 'search' && <div className="space-y-4" data-id="element-299">
            <div className="relative" data-id="element-300">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} data-id="element-301" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSearch()} placeholder="Search by username..." className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500" data-id="element-302" />
            </div>
            <Button fullWidth onClick={handleSearch} data-id="element-303">
              Search
            </Button>

            {searchResults.length > 0 ? <div className="space-y-2" data-id="element-304">
                {searchResults.map(user => <Card key={user.id} data-id="element-305">
                    <div className="flex items-center gap-4" data-id="element-306">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center text-2xl" data-id="element-307">
                        {user.avatar}
                      </div>
                      <div className="flex-1" data-id="element-308">
                        <h3 className="font-bold text-slate-900" data-id="element-309">
                          {user.name}
                        </h3>
                        <p className="text-xs text-slate-500" data-id="element-310">
                          Level {user.level} • {user.wins} wins
                        </p>
                      </div>
                      <Button size="sm" onClick={() => handleAddFriend(user.id, user.name)} data-id="element-311">
                        <UserPlus size={16} className="mr-1" data-id="element-312" />
                        Add
                      </Button>
                    </div>
                  </Card>)}
              </div> : searchQuery && <div className="text-center py-12" data-id="element-313">
                  <p className="text-slate-500" data-id="element-314">No players found</p>
                </div>}

            {!searchQuery && searchResults.length === 0 && <div className="text-center py-12" data-id="element-315">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-316">
                  <Search className="text-slate-400" size={40} data-id="element-317" />
                </div>
                <p className="text-slate-500" data-id="element-318">
                  Search for players to add as friends
                </p>
              </div>}
          </div>}
      </div>

      {/* Add Friend Button */}
      <div className="bg-white border-t border-slate-200 p-4" data-id="element-319">
        <Button fullWidth size="lg" className="gap-2" onClick={() => setActiveTab('search')} data-id="element-320">
          <UserPlus size={20} data-id="element-321" />
          Add Friend
        </Button>
      </div>

      {/* Chat Modal */}
      {selectedFriend && <ChatScreen isOpen={chatOpen} onClose={() => setChatOpen(false)} friendName={selectedFriend.name} friendAvatar={selectedFriend.avatar} data-id="element-322" />}
    </div>;
}